/* eslint-disable react/no-array-index-key */
import { PropTypes } from "prop-types";
import { Button, IconButton, Alert, AlertTitle, Link } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { Country, State } from "country-state-city";
import { branches } from "../../constants/enums";
import ProfilePicture from "../ProfilePicture/ProfilePicture";
import { LargeAvatar } from "../../styles/materialUi/avatar";
import "./alumProfile.scss";
import ProfileSection from "../ProfileSection/ProfileSection";
import ProfileSubSection from "../ProfileSubSection/ProfileSubSection";

const propTypes = {
  cohort: PropTypes.objectOf(PropTypes.any).isRequired,
  alumniProfile: PropTypes.objectOf(PropTypes.any).isRequired,
  createProfile: PropTypes.bool.isRequired,
  isMyProfilePage: PropTypes.bool.isRequired,
  setIsEditMode: PropTypes.func.isRequired,
};

const AlumProfile = ({
  cohort,
  alumniProfile,
  createProfile,
  isMyProfilePage,
  setIsEditMode,
}) => {
  const displayLocation = () => {
    const location = [];
    if (alumniProfile.cityName) location.push(alumniProfile.cityName);
    if (alumniProfile.stateCode) {
      location.push(
        State.getStateByCodeAndCountry(
          alumniProfile.stateCode,
          alumniProfile.countryCode
        ).name
      );
    }
    if (alumniProfile.countryCode) {
      location.push(Country.getCountryByCode(alumniProfile.countryCode).name);
    }
    if (location.length > 0) {
      return <p className="location">{location.join(", ")}</p>;
    }
    return null;
  };

  return (
    <>
      {createProfile ? (
        <div className="create-profile-btn-div">
          <Button
            color="secondary"
            variant="contained"
            onClick={() => setIsEditMode(true)}
          >
            Create your profile
          </Button>
        </div>
      ) : (
        <div className="alum">
          <ProfilePicture
            avatar={LargeAvatar}
            editable={isMyProfilePage}
            profileId={alumniProfile.id}
            objectKey={alumniProfile.profilePictureKey}
            alt={alumniProfile.firstNameDisplay || ""}
          />
          <div className="profile-summary-card">
            {isMyProfilePage && (
              <div className="edit-icon-div">
                <IconButton
                  aria-label="edit"
                  onClick={() => setIsEditMode(true)}
                  size="large"
                >
                  <EditOutlinedIcon fontSize="large" />
                </IconButton>
              </div>
            )}
            <div className="name">
              <h4>
                {alumniProfile.firstNameDisplay} {alumniProfile.lastNameDisplay}
                {alumniProfile.email && (
                  <Link href={`mailto:${alumniProfile.email}`}>
                    <IconButton aria-label="Send Email">
                      <EmailOutlinedIcon />
                    </IconButton>
                  </Link>
                )}
              </h4>
            </div>
            <h5>{alumniProfile.headline}</h5>
            <div className="cohort-branch">
              <h6>
                {cohort.name} {cohort.year} &#8226;{" "}
                {branches[alumniProfile.branch]}
                {alumniProfile.isMilitarySpouse ? " (Military Spouse)" : ""}
              </h6>
            </div>
            {displayLocation()}
            {alumniProfile.linkedInUrl && (
              <Link
                href={alumniProfile.linkedInUrl}
                underline="hover"
                target="_blank"
                rel="noopener"
                className="linkedin-link"
              >
                View profile on LinkedIn
              </Link>
            )}
            <div>
              {alumniProfile.isOpenToWork && (
                <div className="check-container">
                  <CheckCircleOutlineRoundedIcon className="check-icon" />
                  <p>Open to Work</p>
                </div>
              )}
              {alumniProfile.isWillingToTravel && (
                <div className="check-container">
                  <CheckCircleOutlineRoundedIcon className="check-icon" />
                  <p>Willing to Travel</p>
                </div>
              )}
            </div>
          </div>
          {alumniProfile.about ? (
            <ProfileSection
              title="About"
              content={<p>{alumniProfile.about}</p>}
            />
          ) : null}
          {alumniProfile.experiences?.length ? (
            <ProfileSection
              title="Experience"
              content={alumniProfile.experiences.map((experience, i) => (
                <ProfileSubSection
                  key={i}
                  index={i}
                  alt={experience.company}
                  logoUrl={experience.logoUrl}
                  detailTitle={experience.company}
                  detailSubTitle={experience.title}
                  startsAt={experience.startsAt}
                  endsAt={experience.endsAt}
                  description={experience.description}
                />
              ))}
            />
          ) : null}
          {alumniProfile.educations?.length ? (
            <ProfileSection
              title="Education"
              content={alumniProfile.educations.map((edu, i) => (
                <ProfileSubSection
                  key={i}
                  index={i}
                  alt={edu.school}
                  logoUrl={edu.logoUrl}
                  detailTitle={edu.school}
                  detailSubTitle={`${edu.degreeName || ""}${
                    edu.degreeName && edu.fieldOfStudy ? ", " : ""
                  }${edu.fieldOfStudy || ""}`}
                  startsAt={edu.startsAt}
                  endsAt={edu.endsAt}
                  description={edu.description}
                />
              ))}
            />
          ) : null}
          {alumniProfile.certifications?.length ? (
            <ProfileSection
              title="Certifications"
              content={alumniProfile.certifications.map((cert, i) => (
                <ProfileSubSection
                  key={i}
                  index={i}
                  alt={cert.authority}
                  logoUrl=""
                  detailTitle={cert.name}
                  detailSubTitle={cert.authority}
                  startsAt={cert.startsAt}
                  endsAt={cert.endsAt}
                  description={`Credential ID: ${cert.licenseNumber}`}
                />
              ))}
            />
          ) : null}
          {alumniProfile.skills?.length ? (
            <ProfileSection
              title="Skills"
              content={alumniProfile.skills.map((skill) => (
                <p key={skill}>{skill}</p>
              ))}
            />
          ) : null}
          {isMyProfilePage && !alumniProfile.publicProfile && (
            <Alert variant="outlined" severity="info">
              <AlertTitle>Your profile is not public.</AlertTitle>
              Your profile is only visible to admin and the other alumni.
            </Alert>
          )}
        </div>
      )}
    </>
  );
};

AlumProfile.propTypes = propTypes;

export default AlumProfile;
